import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonRounded, Caption, H1 } from '@comeon/ui';
import { selectCurrency, selectLocale } from '~/components/account/ducks/index.js';
import Collapse from './collapse.js';
import useTranslations from '../../../translation/use-translations';
import { Modal } from '../../../modal/modal-old.js';
import BinaryQuestion from '../../../binary-question';
import useIsMounted from '~/hooks/use-is-mounted';
import useIntervalAction from '../../../../hooks/use-interval-action.js';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import IconBottom from '../components/icon-bottom.js';
import {
	fetchGameRoom,
	updateBuyIn,
	FETCH_GAME_ROOM,
	spectatorUnregister,
	fetchPlayersBoughtIntoSession
} from '../ducks/we-spin-duck.js';
import {
	selectConfiguration,
	selectGameRoom,
	selectPlayersBoughtIntoSession,
	selectSessionForStreamRef
} from '../ducks/we-spin-selectors.js';

import { ROUTE_WE_SPIN_GAME_VIEW, ROUTE_WE_SPIN_LOBBY } from '../constants/we-spin-route-constants.js';

import { PREVIEW_STATES, PENDING_START, RUNNING, ENDED } from '../constants/we-spin-stream-states.js';
import {
	WE_SPIN_PREVIEW_ROOM_TEXT_KEYS,
	WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_TOO_LOW_TEXT,
	WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_OVER_CAP_TEXT,
	WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_BALANCE_TOO_LOW_TEXT
} from '../constants/we-spin-text-keys.js';
import { FREE_WESPIN, LABEL_MAPPING } from '../constants/we-spin-boost-constants.js';
import { fetchCurrentEvent } from '../ducks/we-spin-home-game-duck.js';
import WeSpinGameRoomSelectedGames from './we-spin-game-room-selected-games.js';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';

const ADJUST_REASON_OVER_CAP = 'overcap';
const ADJUST_REASON_TOO_LOW = 'toolow';
const ADJUST_REASON_BALANCE_TOO_LOW = 'balanceTooLow';
const ADJUST_REASON_NONE = '';

const ADJUST_REASON_TEXTKEY_MAP = {
	[ADJUST_REASON_TOO_LOW]: WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_TOO_LOW_TEXT,
	[ADJUST_REASON_OVER_CAP]: WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_OVER_CAP_TEXT,
	[ADJUST_REASON_BALANCE_TOO_LOW]: WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_BALANCE_TOO_LOW_TEXT
};

const WeSpinGameRoomPreview = ({ streamRef, streamView }) => {
	const location = useLocation();
	const router = useRouter();
	const isMounted = useIsMounted();
	const { weSpinViewLiveStreamDataPollInterval, hasBetSizeRange, hasMultipleGamesSelection } =
		useSelector(selectConfiguration);
	useIntervalAction(
		`${FETCH_GAME_ROOM}/${streamRef}`,
		fetchGameRoom(streamRef),
		weSpinViewLiveStreamDataPollInterval
	);

	const [showRefundConfirmation, setShowRefundConfirmation] = useState(false);
	const [showAdjustAmountConfirmationReason, setShowAdjustAmountConfirmationReason] = useState(ADJUST_REASON_NONE);

	const dispatch = useDispatch();
	useEffect(() => {
		if (streamRef) {
			dispatch(fetchPlayersBoughtIntoSession(streamRef));
		}
	}, [dispatch, streamRef]);

	const playersBoughtIntoSession = useSelector(selectPlayersBoughtIntoSession(streamRef)) || null;

	const doShowRefundConfirmation = useCallback(() => setShowRefundConfirmation(true), []);
	const doHideRefundConfirmation = useCallback(() => setShowRefundConfirmation(false), []);
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_PREVIEW_ROOM_TEXT_KEYS);

	const doUnregister = useCallback(() => {
		dispatch(spectatorUnregister(streamRef));
		dispatch(fetchCurrentEvent());
		setShowRefundConfirmation(false);
	}, [streamRef, dispatch]);

	const [yourBalance, setYourBalance] = useState(undefined);
	const [showEditAmountQuestion, setShowEditAmountQuestion] = useState(false);

	const onEditAmountQuestionNoCallback = useCallback(() => {
		setShowEditAmountQuestion(false);
	}, []);

	const onChangeEditAmountHandler = useCallback(e => {
		setYourBalance(e.target.value);
	}, []);
	const gameRoom = useSelector(selectGameRoom(streamRef));

	const { yourBuyIn } = gameRoom || {};
	// const { myRaffleTicketCount } = gameRoom || {}; // Hide Raffles

	const doShowEditAmountQuestionCallback = useCallback(() => {
		setShowEditAmountQuestion(true);
	}, [setShowEditAmountQuestion]);

	const onChangeBuyinError = useCallback(
		(reason, amount) => {
			if (isMounted.current) {
				setYourBalance(amount);
				setShowAdjustAmountConfirmationReason(reason);
			}
		},
		[isMounted]
	);

	const doHideAdjustConfirmation = useCallback(() => {
		setShowAdjustAmountConfirmationReason(ADJUST_REASON_NONE);
	}, []);

	const doChangeYourBuyInCallback = useCallback(() => {
		setShowEditAmountQuestion(false);
		doHideAdjustConfirmation();
		dispatch(
			updateBuyIn(
				streamRef,
				yourBalance,
				({ amount }) => onChangeBuyinError(ADJUST_REASON_OVER_CAP, amount),
				({ amount }) => onChangeBuyinError(ADJUST_REASON_TOO_LOW, amount),
				({ amount }) => onChangeBuyinError(ADJUST_REASON_BALANCE_TOO_LOW, amount)
			)
		).catch(() => {}); // saves an error in Chrome
	}, [dispatch, streamRef, yourBalance, onChangeBuyinError, doHideAdjustConfirmation]);

	const { balance, noParticipants, boost, betSizeRangeMin, betSizeRangeMax, currencyCode, games } = streamView || {};

	const isReady = !isLoadingTranslations && gameRoom && !!streamView;
	const isFreeWespin = boost?.type === FREE_WESPIN;

	const locale = useSelector(selectLocale);
	const playerCurrency = useSelector(selectCurrency);

	const streamViewBalanceFormatted = useMemo(
		() => isReady && currencyFormatter.format(balance, playerCurrency, locale),
		[balance, isReady, locale, playerCurrency]
	);

	const yourBuyInFormatted = useMemo(
		() => isReady && currencyFormatter.format(yourBuyIn, playerCurrency, locale),
		[playerCurrency, isReady, locale, yourBuyIn]
	);

	const yourBalanceFormatted = useMemo(
		() => (isReady && yourBalance ? currencyFormatter.format(yourBalance, playerCurrency, locale) : ''),
		[playerCurrency, isReady, locale, yourBalance]
	);

	const gotoLobby = useCallback(() => router.replace(ROUTE_WE_SPIN_LOBBY), [router]);
	const sessionRef = useSelector(selectSessionForStreamRef(streamRef))?.sessionRef;
	const gotoStream = useCallback(
		() => router.replace(`${ROUTE_WE_SPIN_GAME_VIEW}/${sessionRef}`),
		[sessionRef, router]
	);

	if (!isReady) return null;
	return (
		<>
			<Modal show={showEditAmountQuestion}>
				<div className="form">
					<BinaryQuestion
						shouldOpenBinary
						title={tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_TITLE}
						text={tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_TEXT}
						yesText={tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_YES_BUTTON_TEXT}
						onYesClick={doChangeYourBuyInCallback}
						noText={tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_NO_BUTTON_TEXT}
						renderInParent
						onNoClick={onEditAmountQuestionNoCallback}
						onOutsideClick={onEditAmountQuestionNoCallback}
						id="edit-wespin-buyin"
						className="edit-wespin-buyin"
						hasInput
						inputValue={yourBalance}
						inputType="number"
						inputPattern="\d+"
						min="0.01"
						maxlength={20}
						placeholder={tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_PLACEHOLDER}
						inputLabel=""
						onChange={onChangeEditAmountHandler}
						enableInputValidation
					/>
				</div>
			</Modal>
			<Modal
				show={
					!PREVIEW_STATES.includes(streamView.state) &&
					!location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW) &&
					!streamView.isPrivateStreamer
				}
				position="fixed"
			>
				<div className="form">
					<BinaryQuestion
						shouldOpenBinary
						title={tr.WE_SPIN_PREVIEW_ROOM_LIVE_STREAM_DIALOG_TITLE}
						text={tr.WE_SPIN_PREVIEW_ROOM_LIVE_STREAM_DIALOG_TEXT}
						yesText={tr.WE_SPIN_PREVIEW_ROOM_LIVE_STREAM_DIALOG_YES_BUTTON}
						onYesClick={gotoStream}
						noText={tr.WE_SPIN_PREVIEW_ROOM_LIVE_STREAM_DIALOG_NO_BUTTON}
						renderInParent
						onNoClick={gotoLobby}
					/>
				</div>
			</Modal>
			<Modal show={showRefundConfirmation} position="fixed">
				<div className="form">
					<BinaryQuestion
						shouldOpenBinary
						title={!isFreeWespin ? tr.WE_SPIN_PREVIEW_ROOM_REFUND_DIALOG_TITLE : 'Unregister'}
						text={
							!isFreeWespin
								? tr.WE_SPIN_PREVIEW_ROOM_REFUND_DIALOG_TEXT
								: 'Are you sure you want to leave this event?'
						}
						yesText={tr.WE_SPIN_PREVIEW_ROOM_REFUND_DIALOG_YES_BUTTON}
						onYesClick={doUnregister}
						noText={tr.WE_SPIN_PREVIEW_ROOM_REFUND_DIALOG_NO_BUTTON}
						renderInParent
						onNoClick={doHideRefundConfirmation}
					/>
				</div>
			</Modal>

			<Modal show={showAdjustAmountConfirmationReason !== ADJUST_REASON_NONE} position="fixed">
				<div className="form">
					<BinaryQuestion
						shouldOpenBinary
						title={tr.WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_TITLE}
						text={tr[ADJUST_REASON_TEXTKEY_MAP[showAdjustAmountConfirmationReason]]}
						yesText={tr.WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_CONFIRM}
						onYesClick={doChangeYourBuyInCallback}
						noText={tr.WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_CANCEL}
						renderInParent
						onNoClick={doHideAdjustConfirmation}
						onOutsideClick={doHideAdjustConfirmation}
						openInSidebar
					>
						<div className="u-pt2">
							<table className="table--binary-question">
								<tbody>
									<tr>
										<td>{tr.WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_CURRENT_BALANCE}</td>
										<td>{yourBuyInFormatted}</td>
									</tr>
									<tr>
										<td>{tr.WE_SPIN_PREVIEW_ROOM_ADJUST_AMOUNT_NEW_AMOUNT}</td>
										<td>{yourBalanceFormatted}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</BinaryQuestion>
				</div>
			</Modal>

			<div className="u-p1 u-flex-available-height-column wespin-gameroom-preview">
				{boost && (
					<Collapse
						label={LABEL_MAPPING[boost.type]}
						text={boost.description}
						dataAt="collapse-button-games-excluded-bonus"
						value={currencyFormatter.format(boost.amount, boost.currency, locale)}
						infoIcon
						iconBottom={<IconBottom />}
					/>
				)}
				<Collapse
					label={tr.WE_SPIN_PREVIEW_ROOM_WE_SPIN_BALANCE_LABEL}
					text={tr.WE_SPIN_PREVIEW_ROOM_WE_SPIN_BALANCE_TEXT}
					value={streamViewBalanceFormatted}
					infoIcon
					iconBottom={<IconBottom />}
				/>
				{hasBetSizeRange && (
					<Collapse
						label={tr.WE_SPIN_BET_SIZE_RANGE_LABEL}
						text={tr.WE_SPIN_BET_SIZE_RANGE_TEXT}
						value={[betSizeRangeMin, betSizeRangeMax]
							.map(v => (v ? `${v} ${currencyCode}` : '###'))
							.join(' - ')}
						infoIcon
						iconBottom={<IconBottom />}
					/>
				)}
				{hasMultipleGamesSelection && <WeSpinGameRoomSelectedGames games={games} translations={tr} />}
				<Collapse
					label={tr.WE_SPIN_PREVIEW_ROOM_PLAYERS_LABEL}
					text={
						(playersBoughtIntoSession && playersBoughtIntoSession.join(', ')) ||
						tr.WE_SPIN_BUY_IN_PLAYERS_TEXT
					}
					value={noParticipants}
					infoIcon
					iconBottom={<IconBottom />}
				/>
				{/* {myRaffleTicketCount > 0 && (
					<Collapse
						label={tr.WE_SPIN_YOUR_RAFFLE_LABEL}
						text={tr.WE_SPIN_YOUR_RAFFLE_TICKET_INFO}
						value={myRaffleTicketCount}
						infoIcon
						iconBottom={<IconBottom />}
					/>
				)} // Hide Raffles */}
				{streamView.state !== PENDING_START && streamView.state !== RUNNING && streamView.state !== ENDED && (
					<div className="info-box edit-buy-in u-mt1 u-b-radius u-text-c">
						{!isFreeWespin && (
							<>
								<Caption>{tr.WE_SPIN_PREVIEW_ROOM_MY_BALANCE_LABEL}</Caption>
								<H1>{yourBuyInFormatted}</H1>
								<ButtonRounded
									disabled={false}
									small
									smallfont
									onClick={doShowEditAmountQuestionCallback}
									className="btn btn--small edit-buyin-button"
								>
									{tr.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT}
								</ButtonRounded>
							</>
						)}
						<ButtonRounded
							disabled={false}
							small
							smallfont
							onClick={doShowRefundConfirmation}
							className="btn btn--small edit-buyin-button"
						>
							{!isFreeWespin ? tr.WE_SPIN_PREVIEW_ROOM_REFUND_BUTTON : 'Unregister'}
						</ButtonRounded>
						<Caption>{tr.WE_SPIN_PREVIEW_ROOM_REFUND_INFO}</Caption>
					</div>
				)}
			</div>
		</>
	);
};

export default WeSpinGameRoomPreview;
